import React from 'react'
import { Link } from '@inertiajs/react'
import { ResponsiveContainer, LineChart, Line, YAxis, XAxis, Tooltip, ReferenceLine } from 'recharts'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoney } from '@/lib/format'

interface RevparTrend {
  propertyId: number
  propertyName: string
  /** The canonical path of this hotel — backend-served, never rebuilt from id. */
  propertyBasePath: string
  currency: string
  points: { date: string; revpar: number }[]
}

interface RevparSmallMultiplesProps {
  trends: RevparTrend[]
}

function avgRevpar(points: RevparTrend['points']): number {
  if (points.length === 0) return 0
  const sum = points.reduce((acc, p) => acc + p.revpar, 0)
  return sum / points.length
}

export default function RevparSmallMultiples({ trends }: RevparSmallMultiplesProps) {
  // Shared y-scale across all properties for honest comparison
  const allValues = trends.flatMap((t) => t.points.map((p) => p.revpar))
  const max = Math.max(...allValues, 1)
  const ymax = Math.ceil(max / 10) * 10 + 10

  return (
    <div className={`grid gap-4 ${trends.length === 1 ? 'lg:grid-cols-1' : trends.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-3'}`}>
      {trends.map((trend) => {
        const avg = avgRevpar(trend.points)
        const sym = currencySymbol(trend.currency)
        const last = trend.points[trend.points.length - 1]?.revpar ?? 0
        return (
          <Link
            key={trend.propertyId}
            href={trend.propertyBasePath}
            className="group block overflow-hidden rounded-xl border border-border/60 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  RevPAR · 30 days
                </div>
                <h3 className="mt-1 truncate text-sm font-semibold tracking-tight text-foreground">
                  {trend.propertyName}
                </h3>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Avg
                </div>
                <div className="mt-0.5 text-base font-semibold tabular-nums text-foreground">
                  {formatMoney(Math.round(avg * 100) / 100, trend.currency)}
                </div>
              </div>
            </div>

            <div className="mt-4 h-24">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 300, height: 96 }}>
                <LineChart data={trend.points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <YAxis domain={[0, ymax]} hide />
                  <XAxis dataKey="date" hide />
                  <ReferenceLine
                    y={avg}
                    stroke="#94a3b8"
                    strokeDasharray="2 2"
                    strokeWidth={1}
                  />
                  <Tooltip
                    cursor={{ stroke: '#0d9488', strokeWidth: 1 }}
                    contentStyle={{
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                      fontSize: '11px',
                      padding: '6px 10px',
                    }}
                    labelFormatter={(d: any) =>
                      new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })
                    }
                    formatter={(value: any) => [`${sym}${Number(value).toFixed(2)}`, 'RevPAR']}
                  />
                  <Line
                    type="monotone"
                    dataKey="revpar"
                    stroke="#0d9488"
                    strokeWidth={1.75}
                    dot={false}
                    activeDot={{ r: 3, fill: '#0d9488', strokeWidth: 0 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="mt-3 flex items-baseline justify-between border-t border-border/40 pt-3">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Latest
              </span>
              <span className="text-sm font-semibold tabular-nums text-foreground">
                {formatMoney(last, trend.currency)}
              </span>
            </div>
          </Link>
        )
      })}
    </div>
  )
}
